import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import type { CallTranscriptDetail } from "../types/transcriptSearch";

// =============================================================================
// useCallTranscript
// =============================================================================
// PURPOSE: Fetch a single call transcript by recording ID for the modal panel.
//
// ARCHITECTURE:
// - Calls get_call_transcript RPC with p_recording_id, p_organization_id.
// - RPC returns a single jsonb object (not an array). Supabase surfaces it as
//   data: CallTranscriptDetail | null.
// - Stale-request guard: requestIdRef increments on each call; stale responses
//   are discarded so a slow earlier response never overwrites a faster later one.
// - No debounce — fetch fires when the modal opens (recordingId becomes non-null),
//   not on every keystroke.
//
// KEY DECISIONS:
// - NULL data (recording not found / not owned by this org) → transcript=null,
//   NOT an error state. The modal can handle "no transcript" gracefully.
// - orgId + recordingId guard: either null → skip fetch, clear state.
// - async fn defined inside effect (not useCallback) — same pattern as
//   useCohortRevenueStack; satisfies react-hooks/set-state-in-effect.
// =============================================================================

export function useCallTranscript(
  recordingId: number | null,
  orgId: string | null
): {
  transcript: CallTranscriptDetail | null;
  loading: boolean;
  error: string | null;
} {
  const [transcript, setTranscript] = useState<CallTranscriptDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monotonically increasing counter — discard any response that isn't current.
  const requestIdRef = useRef(0);

  // ── Trigger fetch ────────────────────────────────────────────────────────────
  // ALL setState calls live inside the async `run()` to satisfy
  // react-hooks/set-state-in-effect — including the null-guard early exit.
  useEffect(() => {
    const reqId = ++requestIdRef.current;

    async function run() {
      if (recordingId == null || orgId == null) {
        setTranscript(null);
        setLoading(false);
        return;
      }
      if (!supabase) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc("get_call_transcript", {
        p_recording_id: recordingId,
        p_organization_id: orgId,
      });

      // Discard stale responses — another call was made while this one was in flight.
      if (reqId !== requestIdRef.current) return;

      if (rpcError) {
        logger.error("useCallTranscript:", rpcError);
        setError(rpcError.message);
        setTranscript(null);
      } else {
        // data is null when the recording doesn't exist or doesn't belong to this org.
        // That's a valid "not found" state, not an error.
        setTranscript((data as CallTranscriptDetail) ?? null);
      }

      setLoading(false);
    }

    void run();
  }, [recordingId, orgId]);

  return { transcript, loading, error };
}
